import { Injectable } from '@angular/core';
import { ValidationErrors, AsyncValidatorFn, AbstractControl, FormControl } from '@angular/forms';
import { map, delay, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import { isWhitheSpaces } from '@commons/utility/util';

import { ValidatorService } from '@core/components/validator.service';
import { PhoneValidationResult } from '@core/model/phone-validation-result';
import { ResponseValidation } from '@core/model/response/response-validation';
import { CustomResponseDto } from '@core/model/custom-response-dto';

@Injectable({
  providedIn: 'root',
})
export class AsyncValidatorService {
  errorMessage: string;

  constructor(private validatorService: ValidatorService) {}

  public asyncValidatePhone(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return of(control.value).pipe(
        delay(100),
        switchMap((value) => {
          if (value) {
            return this.asyncValidatePhoneNumber(value);
          }
        })
      );
    };
  }

  public asyncValidateIncome(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return of(control.value).pipe(
        delay(100),
        switchMap((value) => {
          if (value) {
            return this.asyncValidateMinimumIncome(value);
          }
        })
      );
    };
  }

  public asyncValidateExpense(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return of(control.value).pipe(
        delay(100),
        switchMap((value) => {
          if (value) {
            return this.asyncValidateMinimalMaximumExpense(value);
          }
        })
      );
    };
  }

  public asyncValidateFullName(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const isWhitespace = isWhitheSpaces(control.value);
      if (!isWhitespace) {
        return this.validatorService.nameOrLastNameIsValid(control.value).pipe(
          map((response: CustomResponseDto<boolean>) => {
            this.errorMessage = response.message;
            return response.response == false ? { invalidFullName: true } : null;
          })
        );
      } else {
        return of(null);
      }
    };
  }

  private asyncValidatePhoneNumber(mobileNumber: string): Observable<ValidationErrors | null> | null {
    return this.validatorService.validatePhoneNumber(mobileNumber).pipe(
      map((response: PhoneValidationResult) => {
        this.errorMessage = response.message;
        return response.result;
      })
    );
  }

  private asyncValidateMinimumIncome(income: string): Observable<ValidationErrors | null> | null {
    return this.validatorService.validateMinimalIncome(income).pipe(
      map((response: ResponseValidation) => {
        this.errorMessage = response.message;
        return response.result;
      })
    );
  }

  private asyncValidateMinimalMaximumExpense(expense: string): Observable<ValidationErrors | null> | null {
    return this.validatorService.validateMinimalMaximaMaximumExpense(expense).pipe(
      map((response: ResponseValidation) => {
        this.errorMessage = response.message;
        return response.result;
      })
    );
  }
}

export function MinimalCurrencyValidation(validatorService: ValidatorService) {
  return {
    validationMessages: [{ name: 'currencyMinimalValue', message: 'Ingreso menor que el valor mínimo' }],
    validators: [
      {
        name: 'currencyMinimalValue',
        validation: (control: FormControl) => {
          return validatorService
            .validateMinimalIncome(control.value)
            .pipe(map((isValid) => (isValid ? null : { currencyMinimalValue: true })));
        },
      },
    ],
  };
}
